import { useEffect, useState, useMemo, useCallback } from "react";
import { Col } from "reactstrap";
import { useMediaQuery } from "react-responsive";
import "bulma/css/bulma.css";
import "../../../../../../../index.css";
import Swal from "sweetalert2";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";
import { useLocation, useNavigate, Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faMagnifyingGlass,
  faPenToSquare,
  faTrash,
  faListUl,
} from "@fortawesome/free-solid-svg-icons";
import ToastSuccess from "../../../../../../NotificationToast/ToastSuccess";
import ToastError from "../../../../../../NotificationToast/ToastError";
import Pagination from "../../../../../../components/pagination_new";

// DATA DUMMY SEMENTARA: setiap kategori memiliki data tersendiri berdasarkan ID URL.
// Saat endpoint tersedia, fungsi baca/simpan di bawah bisa diganti dengan API.
const dummyStorageKey = (idKategori) => `sims-komentar-aspek-dummy-v1:${idKategori}`;
const dummyDefault = (idKategori) => [
  { id: `${idKategori}-dummy-1`, id_kategori_komentar: idKategori, aspek: "Contoh 1" },
  { id: `${idKategori}-dummy-2`, id_kategori_komentar: idKategori, aspek: "Contoh 2" },
  { id: `${idKategori}-dummy-3`, id_kategori_komentar: idKategori, aspek: "Contoh 3" },
];

const readDummyData = (idKategori) => {
  if (!idKategori) return [];
  const key = dummyStorageKey(idKategori);
  try {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error("Gagal membaca data dummy komentar aspek:", error);
  }
  return dummyDefault(idKategori);
};

export default function KomentarAspek() {
  document.title = "Komentar Aspek";

  const { id, id_kategori, id_kategori_komentar, id_element } = useParams();
  // Mendukung :id (route baru) sekaligus :id_element (route lama).
  const kategoriId = String(id || id_kategori || id_kategori_komentar || id_element || "");
  const location = useLocation();
  const navigate = useNavigate();
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 780px)" });

  const [getData, setGetData] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);
  const [idUpdate, setIdUpdate] = useState(null);
  const [dataUpdate, setDataUpdate] = useState(null);

  const [permission, setPermission] = useState({
    create: 0,
    read: 0,
    update: 0,
    delete: 0,
  });

  const kategoriNama = useMemo(() => {
    const dariNavigasi = location.state?.kategoriKomentar;
    if (String(dariNavigasi?.id) === kategoriId && dariNavigasi?.name_categories) {
      return dariNavigasi.name_categories;
    }
    try {
      return sessionStorage.getItem(`sims-komentar-kategori-nama:${kategoriId}`) || "";
    } catch (error) {
      return "";
    }
  }, [kategoriId, location.state]);

  // Hak akses mengikuti menu Kategori Komentar; URL detail memiliki parameter ID dinamis.
  useEffect(() => {
    const storageLevel = localStorage.getItem("level");
    let storageItems = [];
    try {
      storageItems = JSON.parse(localStorage.getItem("role_permission")) || [];
    } catch (error) {
      storageItems = [];
    }

    if (storageLevel === "developer" || storageLevel === "superadmin") {
      setPermission({ create: 1, read: 1, update: 1, delete: 1 });
      return;
    }

    const foundPermission = storageItems.find((item) =>
      [
        location.pathname,
        "/rapor-siakad/kategori-komentar/aspek/:id",
        "/rapor-siakad/kategori-komentar/aspek",
        "/rapor-siakad/kategori-komentar",
      ].includes(item.menu?.url)
    );

    if (!foundPermission) {
      navigate("/dashboard");
    } else {
      setPermission({
        create: foundPermission.create,
        read: foundPermission.read,
        update: foundPermission.update,
        delete: foundPermission.delete,
      });
    }
  }, [location.pathname, navigate]);

  // GET dummy: hanya menampilkan aspek yang terkait dengan kategori yang dipilih.
  const GetResponseData = useCallback(() => {
    setGetData(readDummyData(kategoriId));
  }, [kategoriId]);

  useEffect(() => {
    setSearchKeyword("");
    setTablePage(1);
    GetResponseData();
  }, [GetResponseData]);

  // Sementara menggunakan localStorage, sehingga CRUD dapat dicoba tanpa backend.
  const saveDummyData = (nextData) => {
    if (!kategoriId) return false;
    try {
      localStorage.setItem(dummyStorageKey(kategoriId), JSON.stringify(nextData));
      setGetData(nextData);
      return true;
    } catch (error) {
      ToastError.fire({ icon: "error", title: "Gagal menyimpan data dummy di browser" });
      return false;
    }
  };

  const handleAdd = (aspek) => {
    const trimmed = aspek.trim();
    if (!trimmed) return false;
    const newItem = {
      id: `${kategoriId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      id_kategori_komentar: kategoriId,
      aspek: trimmed,
    };
    return saveDummyData([...getData, newItem]);
  };

  const handleUpdate = (aspek) => {
    const trimmed = aspek.trim();
    if (!trimmed || idUpdate == null) return false;
    const updated = getData.map((item) =>
      String(item.id) === String(idUpdate) ? { ...item, aspek: trimmed } : item
    );
    return saveDummyData(updated);
  };

  const deleteById = (idAspek, namaAspek) => {
    Swal.fire({
      title: "Hapus data ini?",
      html: '<p class="sims-delete-description">Data <strong></strong> yang dihapus tidak dapat dikembalikan.</p>',
      icon: "warning",
      customClass: {
        popup: "sims-delete-popup",
        icon: "sims-delete-icon",
        title: "sims-delete-title",
        actions: "sims-delete-actions",
        confirmButton: "sims-delete-confirm",
        cancelButton: "sims-delete-cancel",
        validationMessage: "sims-delete-validation",
      },
      buttonsStyling: false,
      showCancelButton: true,
      confirmButtonText: "Hapus data",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,
      didOpen: (popup) => {
        const nameElement = popup.querySelector(".sims-delete-description strong");
        if (nameElement) nameElement.textContent = namaAspek || "aspek";
      },
      preConfirm: () => {
        const nextData = getData.filter((item) => String(item.id) !== String(idAspek));
        if (!saveDummyData(nextData)) {
          Swal.showValidationMessage("Gagal menghapus data dummy");
          return false;
        }
        return true;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        ToastSuccess.fire({ icon: "success", title: "Aspek berhasil dihapus (dummy)" });
      }
    });
  };

  const viewModalUpdate = (row) => {
    setIdUpdate(row.id);
    setDataUpdate(row);
    setModalUpdate(true);
  };

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );

  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    const rows = keyword
      ? getData.filter((item) => String(item.aspek || "").toLowerCase().includes(keyword))
      : getData;
    return [...rows].sort((a, b) =>
      nameCollator.compare(String(a.aspek || ""), String(b.aspek || ""))
    );
  }, [getData, searchKeyword, nameCollator]);

  const safeRowsPerPage = Math.max(1, Number(rowsPerPage) || 10);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / safeRowsPerPage));

  useEffect(() => {
    setTablePage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const paginatedData = useMemo(() => {
    const start = (tablePage - 1) * safeRowsPerPage;
    return filteredData.slice(start, start + safeRowsPerPage);
  }, [filteredData, tablePage, safeRowsPerPage]);

  return (
    <div className="body sims-page">
      {modalAdd && (
        <ModalAdd
          kategoriId={kategoriId}
          kategoriNama={kategoriNama}
          onSave={handleAdd}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          kategoriId={kategoriId}
          kategoriNama={kategoriNama}
          dataUpdate={dataUpdate}
          onSave={handleUpdate}
          show={modalUpdate}
          onHide={() => setModalUpdate(false)}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faListUl} />
            </span>
            <h4 className="page-title-text">Komentar Aspek</h4>
          </div>

          <div className="ml-auto">
            <button
              type="button"
              className="btn btn-create sims-header-button"
              onClick={() => setModalAdd(true)}
              disabled={!kategoriId || !permission.create}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>{isTabletOrMobile ? "Aspek" : "Tambah Aspek"}</span>
            </button>
          </div>
        </div>

        <nav aria-label="breadcrumb" className="sims-breadcrumb-nav mt-4">
          <ol className="sims-breadcrumb">
            <li className="sims-breadcrumb-item">
              <Link to="/rapor-siakad">Rapor</Link>
            </li>
            <li className="sims-breadcrumb-separator">/</li>
            <li className="sims-breadcrumb-item">
              <Link to="/rapor-siakad/kategori-komentar">Kategori Komentar</Link>
            </li>
            <li className="sims-breadcrumb-separator">/</li>
            <li className="sims-breadcrumb-item active" aria-current="page">
              Komentar Aspek
            </li>
          </ol>
        </nav>
      </div>

      <div className="body-content sims-content">
        <Col xl="12" sm="12" className="sims-column">
          <div className="sims-search-wrap mb-3">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="sims-search-icon" />
            <input
              className="sims-search-input"
              placeholder="Cari aspek..."
              value={searchKeyword}
              onChange={(event) => {
                setSearchKeyword(event.target.value);
                setTablePage(1);
              }}
            />
          </div>

          <div className="assessment-element-info">
            <h5 className="mt-2 mb-2">
              Kategori Komentar: {kategoriNama || (kategoriId ? `ID ${kategoriId}` : "Tidak ditemukan")}
            </h5>
            {/* <p className="mb-3" style={{ fontSize: 12, color: "#777" }}>
              Data dummy sementara, tersimpan di browser ini. Belum terhubung ke API.
            </p> */}
          </div>

          <div className="sims-table-card">
            <div className="sims-table-scroll">
              <table className="sims-table">
                <thead>
                  <tr>
                    <th className="sims-col-number">No</th>
                    <th>Aspek</th>
                    <th className="sims-col-action">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((row, index) => (
                      <tr key={row.id}>
                        <td className="sims-col-number">
                          <span className="sims-number-badge">
                            {(tablePage - 1) * safeRowsPerPage + index + 1}
                          </span>
                        </td>
                        <td><span>{row.aspek || "-"}</span></td>
                        <td className="sims-col-action">
                          <div className="sims-action-wrap">
                            <button
                              type="button"
                              className="sims-action-button sims-action-edit"
                              title="Update Aspek"
                              disabled={!permission.update}
                              onClick={() => viewModalUpdate(row)}
                            >
                              <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                            <button
                              type="button"
                              className="sims-action-button sims-action-delete"
                              title="Hapus Aspek"
                              disabled={!permission.delete}
                              onClick={() => deleteById(row.id, row.aspek)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="sims-empty-state">
                        <div className="module-empty-state-content">Data aspek tidak ditemukan</div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalRows={filteredData.length}
              tablePage={tablePage}
              rowsPerPage={rowsPerPage}
              onPageChange={setTablePage}
              onRowsPerPageChange={(value) => {
                setRowsPerPage(Number(value) || 10);
                setTablePage(1);
              }}
            />
          </div>
        </Col>
      </div>
    </div>
  );
}
